import * as React from 'react';
import cn from 'classnames';
import Wings from './Wings/Wings';
import { Seat, Segment, ServicesMap } from '../../types';
import { GdsServiceById } from '@shared/lib/gdsService';
import { PassengerState } from '../../../Checkout/store/passengers/reducers';
import { ThemeProvider, useTheme } from '../../../theme';
import Row from './Row/Row';
import { getRowsWithDifferentClasses, RowWithParts } from './utils';
import { useContext } from 'react';
import { SeatsRecommendContext } from '../../../Checkout/components/Checkout/SeatsRecommendation/SeatsRecommendation';
import SeatMapError from './SeatMapError/SeatMapError';
import { useConfig } from '../../../context';
import { isSegmentSeatMapTooWide } from '../SeatMapService/Content/utils';
import { SeatMapRestrictions } from '@websky/graphql';

interface Props {
	isActive: boolean;
	segment: Segment;
	passengers: PassengerState[];
	passengerId: string;
	segmentId: string;
	onSelect: (seat: Seat[], passengerId: string) => void;
	onClear?: () => void;
	services: ServicesMap;
	allSeatServices: ServicesMap;
	gdsServiceById: GdsServiceById;
	seatMapRef?: React.RefObject<HTMLDivElement>;
	activeDoubleSeats?: boolean;
	isNewServicePredicate?: (row: RowWithParts, nextRow: RowWithParts) => boolean;
	activeFilters?: SeatMapRestrictions[];
}

const SeatMap: React.FunctionComponent<Props> = React.memo(
	props => {
		const theme = useTheme('SeatMap').SeatMapStyles;

		const {
			global: {
				seatMap: { splitByCabin }
			}
		} = useConfig();

		const seatMapRef = React.useRef<HTMLDivElement>(null);

		const rows = props.segment.decks[0].rows;

		const isSeatMapTooWide = isSegmentSeatMapTooWide(props.segment);
		const isSeatMapSmall = rows.length <= 20 && props.passengers.length > 2;

		const seatRecommendContext = useContext(SeatsRecommendContext);

		const rowsWithDifferentClasses = React.useMemo(
			() => getRowsWithDifferentClasses(rows, splitByCabin, props.isNewServicePredicate),
			[rows, props.isNewServicePredicate]
		);

		const aircraftId = props.segment?.segmentInfo?.aircraft?.id || '';

		return (
			<div
				data-aircraft-id={aircraftId}
				data-test-id="seat-map"
				className={cn(theme.container, {
					[theme.container_small]: isSeatMapSmall,
					[theme.seatMap_wide]: isSeatMapTooWide
				})}
				ref={props.seatMapRef}
			>
				<SeatMapError />
				{seatRecommendContext.isOpen && (
					<div className={theme.map__overlay} data-test-id="seats-recommend-overlay" />
				)}

				<div
					className={cn(theme.seatMap__wrapper, {
						[theme.seatMap__wrapper_withoutWings]: false
					})}
					ref={seatMapRef}
				>
					<div className={theme.seatMap}>
						<Wings>
							{rows.map((row, rowIndex) => {
								const isNewServiceClass = rowsWithDifferentClasses.has(row.number);

								return (
									<ThemeProvider
										key={row.number}
										value={{
											SeatMap: {
												SeatStyles: {
													seat: theme.seatStyles__seat
												}
											}
										}}
									>
										<Row
											allSeatServices={props.allSeatServices}
											services={props.services}
											gdsServiceById={props.gdsServiceById}
											isSeatMapWide={isSeatMapTooWide}
											onSelect={props.onSelect}
											passengers={props.passengers}
											nextRow={rows[rowIndex + 1]}
											passengerId={props.passengerId}
											segmentId={props.segment.segmentInfo.id}
											lastRow={rows.length - 1}
											rowIndex={rowIndex}
											row={row}
											newClassType={isNewServiceClass}
											onClear={props.onClear}
											rows={rows}
											activeDoubleSeats={props.activeDoubleSeats}
											rowsWithDifferentClasses={rowsWithDifferentClasses}
											activeFilters={props.activeFilters}
										/>
									</ThemeProvider>
								);
							})}
						</Wings>
					</div>
				</div>
			</div>
		);
	},
	(prevProps, nextProps) => !nextProps.isActive
);

export default SeatMap;
